import React, { useEffect, useRef, useState } from 'react';
import { Box, Button, IconButton, Stack, Tooltip, Typography } from '@mui/material';
import { AssignmentOutlined, PendingOutlined, RuleOutlined, TaskAltOutlined } from '@mui/icons-material';
import { libraryTooltipPaperSx } from '../LibraryTooltip';

const TIERS = {
  1: { label: 'Minimally defined', description: 'Only the basics are specified.', Icon: RuleOutlined },
  2: { label: 'Partly defined', description: 'Some important details still need definition.', Icon: AssignmentOutlined },
  3: { label: 'Well defined', description: 'The key inputs, instructions, and safeguards are in place.', Icon: TaskAltOutlined },
};

export default function BuildQualityIndicator({ buildQuality, onFindMissing, showLabel = false }) {
  const [clickOpen, setClickOpen] = useState(false);
  const [hoverOpen, setHoverOpen] = useState(false);
  const buttonRef = useRef(null);
  const panelRef = useRef(null);
  const tier = TIERS[buildQuality?.tier];
  const label = tier?.label || 'Not assessed';
  const Icon = tier?.Icon || PendingOutlined;
  const gaps = Array.isArray(buildQuality?.gaps) ? buildQuality.gaps : [];

  useEffect(() => {
    if (!clickOpen) return undefined;
    const closeOnOutsideClick = (event) => {
      if (!buttonRef.current?.contains(event.target) && !panelRef.current?.contains(event.target)) {
        setClickOpen(false);
        setHoverOpen(false);
      }
    };
    const closeOnEscape = (event) => {
      if (event.key === 'Escape') {
        setClickOpen(false);
        setHoverOpen(false);
      }
    };
    document.addEventListener('pointerdown', closeOnOutsideClick);
    document.addEventListener('keydown', closeOnEscape);
    return () => {
      document.removeEventListener('pointerdown', closeOnOutsideClick);
      document.removeEventListener('keydown', closeOnEscape);
    };
  }, [clickOpen]);

  return (
    <Tooltip
      open={clickOpen || hoverOpen}
      onOpen={() => setHoverOpen(true)}
      onClose={() => setHoverOpen(false)}
      placement="bottom-end"
      title={(
        <Box ref={panelRef} sx={{ width: 300, maxWidth: 'calc(100vw - 24px)', p: 1.5 }}>
          <Stack spacing={1}>
            <Typography variant="subtitle2">Build quality: {label}</Typography>
            <Typography variant="body2" color="text.secondary">
              {tier?.description || 'A current assessment is not available yet.'}
            </Typography>
            {gaps.length > 0 && (
              <Box>
                <Typography variant="body2" sx={{ fontWeight: 600, mb: 0.5 }}>To improve:</Typography>
                <Box component="ul" sx={{ pl: 2.5, m: 0 }}>
                  {gaps.map((gap, index) => (
                    <Typography component="li" variant="body2" key={`${gap.kind}-${index}`} sx={{ mb: 0.5 }}>
                      {gap.action}
                    </Typography>
                  ))}
                </Box>
              </Box>
            )}
            {onFindMissing && (
              <Button size="small" onClick={(event) => { event.stopPropagation(); setClickOpen(false); setHoverOpen(false); onFindMissing(); }} sx={{ alignSelf: 'flex-start' }}>
                Improve capability
              </Button>
            )}
          </Stack>
        </Box>
      )}
      componentsProps={{ tooltip: { sx: libraryTooltipPaperSx } }}
    >
      {showLabel ? (
        <Button
          ref={buttonRef}
          size="small"
          aria-label={`Build quality: ${label}`}
          onClick={(event) => { event.stopPropagation(); setClickOpen(true); }}
          startIcon={<Icon sx={{ fontSize: '16px !important', color: 'text.secondary' }} />}
          sx={{ color: 'text.secondary', minWidth: 0, px: 0.5, py: 0.25, fontSize: '0.75rem', fontWeight: 500, textTransform: 'none', whiteSpace: 'nowrap' }}
        >
          {label}
        </Button>
      ) : (
        <IconButton
          ref={buttonRef}
          size="small"
          aria-label={`Build quality: ${label}`}
          onClick={(event) => { event.stopPropagation(); setClickOpen(true); }}
          sx={{ color: 'text.secondary', width: 32, height: 32, p: 0, '& .MuiSvgIcon-root': { fontSize: 16 } }}
        >
          <Icon sx={{ color: 'text.secondary' }} />
        </IconButton>
      )}
    </Tooltip>
  );
}
